import { useState, useEffect, useCallback } from 'react';
import { articleRepository, eventRepository } from '../../../../domain/repositories';

/**
 * Custom hook to manage homepage data (recent events and articles)
 * @param {Object} options - Configuration options
 * @param {number} options.eventsLimit - Number of recent events to fetch (default: 6)
 * @param {number} options.articlesLimit - Number of recent articles to fetch (default: 5)
 * @returns {Object} Homepage state and data
 */
export const useHomePage = ({ eventsLimit = 6, articlesLimit = 5 } = {}) => {
  const [events, setEvents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHomePageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch events and articles in parallel
      // Events are already sorted by date in the repository
      const [eventsData, articlesData] = await Promise.all([
        eventRepository.getLatestEvents(eventsLimit),
        articleRepository.getLatestArticles(articlesLimit)
      ]);
      
      setEvents(eventsData);
      setArticles(articlesData);
    } catch (err) {
      console.error('Error loading homepage data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [eventsLimit, articlesLimit]);

  useEffect(() => {
    loadHomePageData();
  }, [loadHomePageData]);

  const retry = () => {
    loadHomePageData();
  };

  return {
    events,
    articles,
    loading,
    error,
    retry
  };
};
